import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, BarChart3, Layers, GraduationCap, MapPin, Users } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface GroupRow {
  id: string;
  name: string;
  semester: string;
  class: string;
  site_id: string | null;
  site_name_override: string | null;
  field_sites: { id: string; name: string } | null;
}

interface AggRow {
  key: string;
  label: string;
  groups: number;
  students: number;
  scheduled: number;
  present: number;
  rate: number;
}

const ALL = "__all__";

export function RotationReportsView() {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [members, setMembers] = useState<{ group_id: string; student_id: string; created_at: string }[]>([]);
  const [dates, setDates] = useState<{ group_id: string; placement_date: string }[]>([]);
  const [scans, setScans] = useState<{ field_group_id: string; student_id: string; attendance_date: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const [semester, setSemester] = useState<string>(ALL);
  const [klass, setKlass] = useState<string>(ALL);
  const [siteId, setSiteId] = useState<string>(ALL);

  // Today's date in local YYYY-MM-DD form, used to clamp "scheduled" to dates that have actually occurred.
  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const { data: g } = await supabase
        .from("field_groups")
        .select("id, name, semester, class, site_id, site_name_override, field_sites(id,name)")
        .eq("approval_status", "approved")
        .order("name");
      const groupList = (g ?? []) as any as GroupRow[];
      setGroups(groupList);
      const ids = groupList.map((x) => x.id);
      if (!ids.length) { setMembers([]); setDates([]); setScans([]); setLoading(false); return; }
      const [m, d, s] = await Promise.all([
        supabase.from("field_group_members").select("group_id, student_id, created_at").in("group_id", ids),
        supabase.from("field_group_dates").select("group_id, placement_date").in("group_id", ids),
        supabase
          .from("attendance_records")
          .select("field_group_id, student_id, attendance_date")
          .in("field_group_id", ids)
          .eq("attendance_type", "tuition")
          .eq("status", "present"),
      ]);
      setMembers((m.data ?? []) as any);
      setDates((d.data ?? []) as any);
      setScans((s.data ?? []) as any);
      setLoading(false);
    })();
  }, []);

  const semesters = useMemo(() => Array.from(new Set(groups.map((g) => g.semester))).sort(), [groups]);
  const classes = useMemo(() => Array.from(new Set(groups.map((g) => g.class))).sort(), [groups]);
  const sites = useMemo(() => {
    const map = new Map<string, string>();
    groups.forEach((g) => {
      const id = g.site_id ?? `__ovr__${g.site_name_override ?? "adhoc"}`;
      const name = g.field_sites?.name ?? g.site_name_override ?? "Ad-hoc";
      map.set(id, name);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  const filteredGroups = useMemo(() => groups.filter((g) => {
    if (semester !== ALL && g.semester !== semester) return false;
    if (klass !== ALL && g.class !== klass) return false;
    if (siteId !== ALL) {
      const id = g.site_id ?? `__ovr__${g.site_name_override ?? "adhoc"}`;
      if (id !== siteId) return false;
    }
    return true;
  }), [groups, semester, klass, siteId]);

  const groupIdSet = useMemo(() => new Set(filteredGroups.map((g) => g.id)), [filteredGroups]);

  // Build per-group stats.
  //
  // Institution rule: a student is "scheduled" for a placement date only when
  //   (a) the placement date is on or before today (future dates are not yet due), AND
  //   (b) the student was already a member of the group on that date
  //       (membership join date taken from `field_group_members.created_at`).
  //
  // "Present" only counts a scan if it falls on an actual placement date for that
  // group — ad-hoc / out-of-schedule scans never inflate the rate above 100%.
  const perGroup = useMemo<AggRow[]>(() => {
    // group_id -> placement dates that are due (<= today)
    const datesByGroup = new Map<string, string[]>();
    dates.forEach((d) => {
      if (d.placement_date > today) return; // skip future dates
      const arr = datesByGroup.get(d.group_id) ?? [];
      arr.push(d.placement_date);
      datesByGroup.set(d.group_id, arr);
    });
    const validDateSet = new Map<string, Set<string>>();
    datesByGroup.forEach((list, gid) => validDateSet.set(gid, new Set(list)));

    // group_id -> [{ student_id, joined_on (YYYY-MM-DD) }]
    const membersByGroup = new Map<string, { student_id: string; joined_on: string }[]>();
    members.forEach((m) => {
      const list = membersByGroup.get(m.group_id) ?? [];
      list.push({ student_id: m.student_id, joined_on: (m.created_at ?? "").slice(0, 10) });
      membersByGroup.set(m.group_id, list);
    });

    // Dedupe scans per (group, student, date); keep only those landing on a valid placement date.
    const presentByGroup = new Map<string, Set<string>>();
    scans.forEach((s) => {
      const valid = validDateSet.get(s.field_group_id);
      if (!valid || !valid.has(s.attendance_date)) return;
      const key = `${s.student_id}|${s.attendance_date}`;
      const set = presentByGroup.get(s.field_group_id) ?? new Set<string>();
      set.add(key);
      presentByGroup.set(s.field_group_id, set);
    });

    return filteredGroups.map((g) => {
      const dueDates = datesByGroup.get(g.id) ?? [];
      const groupMembers = membersByGroup.get(g.id) ?? [];

      // Scheduled student-days: per member, only count placement dates on/after the member joined.
      // Members who joined late are not penalised for dates they missed before joining.
      let scheduled = 0;
      groupMembers.forEach((mem) => {
        const join = mem.joined_on || "1970-01-01";
        scheduled += dueDates.filter((d) => d >= join).length;
      });

      const present = presentByGroup.get(g.id)?.size ?? 0;
      // Cap present at scheduled so rate ∈ [0, 100] even if a stale scan slipped through.
      const cappedPresent = Math.min(present, scheduled);
      const rate = scheduled > 0 ? Math.round((cappedPresent / scheduled) * 100) : 0;
      const siteLabel = g.field_sites?.name ?? g.site_name_override ?? "Ad-hoc";
      return {
        key: g.id,
        label: `${g.name} — ${g.class} (Sem ${g.semester}) — ${siteLabel}`,
        groups: 1,
        students: groupMembers.length,
        scheduled,
        present: cappedPresent,
        rate,
      };
    }).sort((a, b) => b.rate - a.rate);
  }, [filteredGroups, dates, members, scans, today]);

  function rollup(by: (g: GroupRow) => { key: string; label: string }): AggRow[] {
    const buckets = new Map<string, AggRow>();
    perGroup.forEach((row) => {
      const g = filteredGroups.find((x) => x.id === row.key)!;
      if (!groupIdSet.has(g.id)) return;
      const { key, label } = by(g);
      const existing = buckets.get(key);
      if (existing) {
        existing.groups += 1;
        existing.students += row.students;
        existing.scheduled += row.scheduled;
        existing.present += row.present;
      } else {
        buckets.set(key, { key, label, groups: 1, students: row.students, scheduled: row.scheduled, present: row.present, rate: 0 });
      }
    });
    return Array.from(buckets.values()).map((r) => ({
      ...r,
      rate: r.scheduled > 0 ? Math.round((r.present / r.scheduled) * 100) : 0,
    })).sort((a, b) => b.rate - a.rate);
  }

  const bySemester = useMemo(() => rollup((g) => ({ key: g.semester, label: `Semester ${g.semester}` })), [perGroup]);
  const byClass = useMemo(() => rollup((g) => ({ key: g.class, label: g.class })), [perGroup]);
  const bySite = useMemo(() => rollup((g) => {
    const id = g.site_id ?? `__ovr__${g.site_name_override ?? "adhoc"}`;
    const label = g.field_sites?.name ?? g.site_name_override ?? "Ad-hoc";
    return { key: id, label };
  }), [perGroup]);

  const totals = useMemo(() => {
    const scheduled = perGroup.reduce((s, r) => s + r.scheduled, 0);
    const present = perGroup.reduce((s, r) => s + r.present, 0);
    const students = perGroup.reduce((s, r) => s + r.students, 0);
    return {
      groups: perGroup.length,
      students,
      scheduled,
      present,
      rate: scheduled > 0 ? Math.round((present / scheduled) * 100) : 0,
    };
  }, [perGroup]);

  function exportRows(rows: AggRow[], scope: string) {
    if (!rows.length) return;
    exportToCsv(
      `Rotation_${scope}_${format(new Date(), "yyyy-MM-dd")}.csv`,
      ["#", scope, "Groups", "Students", "Scheduled student-days", "Present", "Rate %"],
      rows.map((r, i) => [String(i + 1), r.label, String(r.groups), String(r.students), String(r.scheduled), String(r.present), String(r.rate)]),
    );
  }

  const renderTable = (rows: AggRow[], scopeLabel: string) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">#</TableHead>
          <TableHead>{scopeLabel}</TableHead>
          <TableHead className="text-right">Groups</TableHead>
          <TableHead className="text-right">Students</TableHead>
          <TableHead className="text-right">Scheduled</TableHead>
          <TableHead className="text-right">Present</TableHead>
          <TableHead className="text-right">Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
        ) : rows.length === 0 ? (
          <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No data for current filters.</TableCell></TableRow>
        ) : rows.map((r, i) => (
          <TableRow key={r.key}>
            <TableCell>{i + 1}</TableCell>
            <TableCell className="font-medium">{r.label}</TableCell>
            <TableCell className="text-right">{r.groups}</TableCell>
            <TableCell className="text-right">{r.students}</TableCell>
            <TableCell className="text-right">{r.scheduled}</TableCell>
            <TableCell className="text-right">{r.present}</TableCell>
            <TableCell className="text-right">
              <Badge variant={r.rate >= 80 ? "default" : r.rate >= 60 ? "secondary" : "destructive"}>{r.rate}%</Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-4 h-4" /> Rotation Reports</CardTitle>
          <CardDescription>
            Attendance rates broken down by semester, class, field group, and site.
            Scheduled student-days only count placement dates that have already occurred and only for members who had joined the group by that date.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Semester</Label>
            <Select value={semester} onValueChange={setSemester}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All semesters</SelectItem>
                {semesters.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Class</Label>
            <Select value={klass} onValueChange={setKlass}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All classes</SelectItem>
                {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" className="w-full" onClick={() => { setSemester(ALL); setKlass(ALL); setSiteId(ALL); }}>
              Reset filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
        <Card><CardContent className="py-3"><p className="text-2xl font-bold">{totals.groups}</p><p className="text-xs text-muted-foreground">Groups</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-2xl font-bold">{totals.students}</p><p className="text-xs text-muted-foreground">Students</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-2xl font-bold">{totals.scheduled}</p><p className="text-xs text-muted-foreground">Scheduled</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-2xl font-bold">{totals.present}</p><p className="text-xs text-muted-foreground">Present</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-2xl font-bold">{totals.rate}%</p><p className="text-xs text-muted-foreground">Overall rate</p></CardContent></Card>
      </div>

      <Tabs defaultValue="semester" className="space-y-3">
        <TabsList>
          <TabsTrigger value="semester"><Layers className="w-4 h-4 mr-1" /> By Semester</TabsTrigger>
          <TabsTrigger value="class"><GraduationCap className="w-4 h-4 mr-1" /> By Class</TabsTrigger>
          <TabsTrigger value="group"><Users className="w-4 h-4 mr-1" /> By Group</TabsTrigger>
          <TabsTrigger value="site"><MapPin className="w-4 h-4 mr-1" /> By Site</TabsTrigger>
        </TabsList>

        <TabsContent value="semester">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Attendance by Semester</CardTitle>
              <Button size="sm" variant="outline" onClick={() => exportRows(bySemester, "Semester")} disabled={!bySemester.length}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </CardHeader>
            <CardContent>{renderTable(bySemester, "Semester")}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="class">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Attendance by Class</CardTitle>
              <Button size="sm" variant="outline" onClick={() => exportRows(byClass, "Class")} disabled={!byClass.length}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </CardHeader>
            <CardContent>{renderTable(byClass, "Class")}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="group">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Attendance by Field Group</CardTitle>
              <Button size="sm" variant="outline" onClick={() => exportRows(perGroup, "Group")} disabled={!perGroup.length}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </CardHeader>
            <CardContent>{renderTable(perGroup, "Group")}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="site">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Attendance by Site</CardTitle>
              <Button size="sm" variant="outline" onClick={() => exportRows(bySite, "Site")} disabled={!bySite.length}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </CardHeader>
            <CardContent>{renderTable(bySite, "Site")}</CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
